import React, { useEffect, useMemo, useState } from "react";
import toast from "react-hot-toast";
import SkeletonList from "../common/SkeletonList";

const getCookie = (name) => {
  const match = document.cookie.match(
    new RegExp("(?:^|; )" + name.replace(/([.$?*|{}()\[\]\\/\+^])/g, "\\$1") + "=([^;]*)")
  );
  return match ? decodeURIComponent(match[1]) : null;
};

const buildImageUrl = (path) => {
  if (!path) return "./chatscreen/profile.svg";
  if (path.startsWith("http://") || path.startsWith("https://")) return path;
  if (path.startsWith("/")) return `${process.env.REACT_APP_API_BASE_URL}${path}`;
  return path;
};

export default function AdminBroadcast() {
  const [contacts, setContacts] = useState([]);
  const [selectedUserIds, setSelectedUserIds] = useState([]);
  const [message, setMessage] = useState("");
  const [search, setSearch] = useState("");
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const pointerStartRef = React.useRef(null);

  const token = getCookie("admin_token");

  const filteredContacts = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return contacts;
    return contacts.filter((user) => {
      const haystack = `${user.name} ${user.phone} ${user.email}`.toLowerCase();
      return haystack.includes(q);
    });
  }, [contacts, search]);

  const allVisibleSelected =
    filteredContacts.length > 0 &&
    filteredContacts.every((user) => selectedUserIds.includes(String(user.id)));

  const fetchContacts = async () => {
    if (!token) {
      setError("Not authenticated");
      return;
    }

    setLoadingUsers(true);
    setError("");
    try {
      const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}/admin/connect/users`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!res.ok) throw new Error("Failed to load users");

      const json = await res.json();
      const list = (json?.data || []).map((u) => ({
        id: u.user_id,
        name: u.full_name || `${u.first_name || ""} ${u.last_name || ""}`.trim() || "Unknown",
        email: u.email || "",
        phone: `${u.country_code || ""}${u.phone_number || ""}`,
        avatar: buildImageUrl(u.profile_image_url),
      }));

      setContacts(list);
    } catch (e) {
      setError(e.message || "Failed to load users");
    } finally {
      setLoadingUsers(false);
    }
  };

  useEffect(() => {
    fetchContacts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const [isMobile, setIsMobile] = useState(
    typeof window !== "undefined" ? window.innerWidth < 768 : false
  );
  const [mobileView, setMobileView] = useState("list"); // 'list' | 'compose'

  useEffect(() => {
    const mm = window.matchMedia("(max-width: 767px)");
    const onChange = (e) => setIsMobile(e.matches);
    if (mm.addEventListener) mm.addEventListener("change", onChange);
    else mm.addListener(onChange);
    setIsMobile(mm.matches);
    return () => {
      if (mm.removeEventListener) mm.removeEventListener("change", onChange);
      else mm.removeListener(onChange);
    };
  }, []);

  useEffect(() => {
    if (isMobile && mobileView === "compose" && messageRef.current) {
      window.requestAnimationFrame(() => {
        messageRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
      });
    }
  }, [isMobile, mobileView, selectedUserIds.length]);

  const toggleUser = (userId) => {
    const id = String(userId);
    setSelectedUserIds((prev) =>
      prev.includes(id) ? prev.filter((v) => v !== id) : [...prev, id]
    );
  };

  const toggleAllVisible = () => {
    if (filteredContacts.length === 0) return;
    const visibleIds = filteredContacts.map((user) => String(user.id));

    if (allVisibleSelected) {
      setSelectedUserIds((prev) => prev.filter((id) => !visibleIds.includes(id)));
      return;
    }

    setSelectedUserIds((prev) => Array.from(new Set([...prev, ...visibleIds])));
  };

  const handleSendBroadcast = async (e) => {
    e?.preventDefault?.();

    if (!token) {
      toast.error("Admin session expired. Please login again.");
      return;
    }

    const trimmed = message.trim();
    if (!trimmed) {
      toast.error("Message is required");
      return;
    }

    setSubmitting(true);
    setError("");

    try {
      const formData = new FormData();
      formData.append("message", trimmed);

      if (selectedUserIds.length > 0) {
        formData.append("user_ids", selectedUserIds.join(","));
      }

      const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}/admin/broadcast/message`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
        },
        body: formData,
      });

      const json = await res.json();
      if (!res.ok || json?.status !== "1") {
        throw new Error(json?.message || "Broadcast failed");
      }

      toast.success(
        selectedUserIds.length > 0
          ? "Broadcast sent to selected users"
          : "Broadcast sent to all users"
      );
      setMessage("");
      setSelectedUserIds([]);
    } catch (e2) {
      const msg = e2.message || "Broadcast failed";
      setError(msg);
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  const messageRef = React.useRef(null);

  const focusMessage = () => {
    if (messageRef.current) {
      messageRef.current.focus();
      messageRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  };

  const shouldIgnoreTap = (event) => {
    const start = pointerStartRef.current;
    if (!start) return false;

    const currentX = Number.isFinite(event?.clientX) ? event.clientX : start.x;
    const currentY = Number.isFinite(event?.clientY) ? event.clientY : start.y;
    const distance = Math.hypot(currentX - start.x, currentY - start.y);

    pointerStartRef.current = null;
    return distance > 10;
  };

  return (
    <div className="flex h-full min-h-0 flex-col overflow-hidden bg-[#F5F4FF] p-3 sm:p-4 md:p-6">
      <div className="mx-auto grid min-h-0 w-full max-w-8xl flex-1 grid-cols-1 gap-4 md:gap-6 min-[1000px]:grid-cols-[360px_1fr]">
        <section
          className={`${isMobile && mobileView === "compose" ? "hidden" : ""} relative flex min-h-0 flex-col overflow-hidden rounded-2xl bg-white p-3 shadow-lg sm:p-4 min-[1000px]:sticky min-[1000px]:top-6 min-[1000px]:h-full`}
        >
          <div className="mb-3 flex shrink-0 items-center justify-between gap-3">
            <h2 className="text-base font-semibold text-gray-800 sm:text-lg">Recipients</h2>
            <button
              type="button"
              onClick={toggleAllVisible}
              className="hidden min-[1000px]:inline-flex rounded-xl border border-[#6D5EF7] px-3 py-1 text-xs text-[#6D5EF7] transition hover:bg-[#6D5EF7] hover:text-white md:text-sm"
            >
              {allVisibleSelected ? "Unselect visible" : "Select visible"}
            </button>
          </div>

          <div className="rounded-[100px] bg-[#797C7B] bg-opacity-[11%] px-4 py-2 mb-3 shrink-0">
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search by name, phone or email"
              className="w-full bg-transparent text-sm outline-none placeholder:text-gray-500"
            />
          </div>

          <div
            className={`${isMobile && mobileView === "compose" ? "hidden" : ""} min-h-0 flex-1 space-y-3 overflow-y-auto overscroll-contain pr-1 pb-2 touch-pan-y [-webkit-overflow-scrolling:touch] [&::-webkit-scrollbar]:w-1 [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-thumb]:bg-gray-400 [&::-webkit-scrollbar-track]:bg-gray-100`}
          >
            {loadingUsers && <SkeletonList count={6} />}

            {!loadingUsers && filteredContacts.length === 0 && (
              <p className="text-sm text-gray-500">No users found.</p>
            )}

            {!loadingUsers &&
              filteredContacts.map((user) => {
                const isChecked = selectedUserIds.includes(String(user.id));

                return (
                  <button
                    key={user.id}
                    type="button"
                    onPointerDown={(e) => {
                      pointerStartRef.current = { x: e.clientX, y: e.clientY };
                    }}
                    onClick={(e) => {
                      if (shouldIgnoreTap(e)) return;
                      toggleUser(user.id);
                    }}
                    className={`w-full text-left flex items-center gap-3 p-3 py-2 rounded-[100px] shadow-[1px_4px_4px_0_rgba(117,117,117,0.55)] border transition-all duration-200 ${
                      isChecked
                        ? "bg-[#4F46E5] bg-opacity-20 border-[#4F46E5]"
                        : "bg-[#797C7B] bg-opacity-[11%] hover:bg-opacity-20 border-transparent"
                    }`}
                    aria-pressed={isChecked}
                    aria-label={`Select ${user.name}`}
                  >
                    <span
                      className={`h-5 w-5 rounded-full inline-flex items-center justify-center flex-shrink-0 transition-colors duration-150 ${
                        isChecked ? "border-2 border-[#6D5EF7] bg-white" : "border border-gray-300 bg-white"
                      }`}
                    >
                      <span className="h-3 w-3 rounded-full bg-white flex items-center justify-center">
                        {isChecked ? <span className="h-2 w-2 rounded-full bg-[#6D5EF7]" /> : null}
                      </span>
                    </span>
                    <img src={user.avatar} alt={user.name} className="h-10 w-10 rounded-full object-cover" />
                    <div className="min-w-0 flex-1">
                      <p className="heading-font truncate">{user.name}</p>
                      <p className="body-font text-xs text-gray-600 truncate">{user.phone || user.email}</p>
                    </div>
                  </button>
                );
              })}
          </div>

          {isMobile && mobileView === "list" && (
            <div className="shrink-0 border-t border-gray-100 bg-white pt-3">
              <button
                type="button"
                onClick={() => setMobileView("compose")}
                className="inline-flex w-full items-center justify-center rounded-xl bg-[#6D5EF7] px-4 py-3 font-semibold text-white shadow-md"
              >
                {selectedUserIds.length > 0
                  ? `Write Message (${selectedUserIds.length})`
                  : "Write Message (all users)"}
              </button>
            </div>
          )}
        </section>

        <section
          className={`${isMobile && mobileView === "list" ? "hidden" : ""} flex min-h-0 flex-col overflow-hidden rounded-2xl border border-white bg-white p-4 shadow-lg sm:p-5 md:p-6 min-[1000px]:h-full`}
        >
          {isMobile && (
            <div className="mb-4 flex shrink-0 items-center justify-between gap-2 border-b border-gray-100 pb-3">
              <button
                type="button"
                onClick={() => setMobileView("list")}
                className="inline-flex items-center rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm"
              >
                ← Back
              </button>
              <button
                type="button"
                onClick={focusMessage}
                className="inline-flex items-center rounded-lg bg-[#6D5EF7] px-3 py-2 text-sm font-medium text-white"
              >
                Focus message
              </button>
            </div>
          )}

          <form onSubmit={handleSendBroadcast} className="flex min-h-0 flex-1 flex-col">
            <div
              className={`min-h-0 flex-1 overflow-y-auto overscroll-contain touch-pan-y [-webkit-overflow-scrolling:touch] max-md:pb-4`}
            >
              <div className="space-y-2">
                <h1 className="text-xl font-bold text-gray-800 sm:text-2xl">Broadcast Message</h1>
                <p className="text-sm text-gray-500">
                  Send to all users by leaving recipients unselected, or choose specific users from the list.
                </p>
              </div>

              <div
                className={`${isMobile && mobileView === "compose" ? "hidden" : "mt-5 grid grid-cols-1 gap-4 md:grid-cols-2"}`}
              >
                <div className="rounded-xl border-2 border-[#7A5AF8] border-opacity-30 bg-[#7A5AF8] bg-opacity-10 p-4">
                  <p className="text-sm text-gray-700">Selected Recipients</p>
                  <p className="aoboshi-one mt-1 text-xl font-bold text-black">{selectedUserIds.length}</p>
                </div>
              </div>

              <div className="mt-4 sm:mt-6">
                <label className="mb-2 block text-sm font-medium text-gray-700" htmlFor="broadcast-message">
                  Message
                </label>
                <textarea
                  id="broadcast-message"
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  placeholder="Write your broadcast message here..."
                  rows={isMobile ? 5 : 8}
                  ref={messageRef}
                  className="w-full resize-none rounded-2xl border border-gray-200 bg-[#FAFAFF] px-4 py-3 outline-none focus:border-[#6D5EF7]"
                />
              </div>

              {error && <p className="mt-3 text-sm text-red-500">{error}</p>}
            </div>

            <div className="shrink-0 border-t border-gray-100 bg-white pt-3 md:pt-4">
              <button
                type="submit"
                disabled={submitting}
                className="submit-button w-full disabled:cursor-not-allowed disabled:opacity-60"
              >
                {submitting ? "Sending..." : "Send Broadcast"}
              </button>
            </div>
          </form>
        </section>
      </div>
    </div>
  );
}
